import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "./api";

// --------------------------------------------------------------------------- //
// Effective cap discovery (`GET /chat/limits`)                                //
// --------------------------------------------------------------------------- //

/** Shipped default for the `chat_message_max_chars` system setting. Used by
 * the composer as the assumed cap before `GET /chat/limits` resolves, and
 * again if that fetch fails — never blocks the composer on the network. */
export const DEFAULT_CHAT_MESSAGE_MAX_CHARS = 32000;

/** The composer's `n / max` counter renders only once the message exceeds
 * this fraction of the effective cap, so short messages never show it. */
const COUNTER_THRESHOLD_RATIO = 0.8;

export const chatLimitsResponseSchema = z.object({
  message_max_chars: z.number().int().positive(),
});

export async function fetchChatLimits(): Promise<number> {
  const { message_max_chars } = await apiClient.get("/chat/limits", chatLimitsResponseSchema);
  return message_max_chars;
}

export const chatMessageLimitQueryKey = ["chat-message-limit"] as const;

/** Reads the effective `chat_message_max_chars` cap via the authenticated,
 * non-admin `/chat/limits` endpoint (mirrors `useModelRoutingMode`). A
 * missing/errored query resolves to `undefined` here — callers apply
 * `DEFAULT_CHAT_MESSAGE_MAX_CHARS` themselves so the fallback is explicit at
 * each call site instead of being silently baked into the hook. */
export function useChatMessageLimit() {
  return useQuery({
    queryKey: chatMessageLimitQueryKey,
    queryFn: fetchChatLimits,
    staleTime: 60_000,
  });
}

/** Counts Unicode code points, unlike `String.length` (UTF-16 code units).
 * Astral-plane characters (most emoji) are 1 code point but 2 UTF-16 units,
 * so `.length` would over-count them relative to the backend's Python
 * `len(str)` — see design.md §4 of `raise-and-surface-chat-message-cap`. */
export function countCodePoints(text: string): number {
  return Array.from(text).length;
}

/** Whether the composer should render the `n / max` counter. */
export function shouldShowCounter(n: number, max: number): boolean {
  return n > COUNTER_THRESHOLD_RATIO * max;
}

/** Whether a message of length `n` (code points) exceeds the effective cap. */
export function isOverCap(n: number, max: number): boolean {
  return n > max;
}

// --------------------------------------------------------------------------- //
// `message_too_long` 422 (`POST /chat`)                                       //
// --------------------------------------------------------------------------- //

/** `detail.code` the backend sets on the HTTP 422 when a chat message
 * exceeds the effective `chat_message_max_chars` cap. */
export const MESSAGE_TOO_LONG_CODE = "message_too_long";

const messageTooLongDetailSchema = z.object({
  code: z.literal(MESSAGE_TOO_LONG_CODE),
  max_chars: z.number(),
  actual_chars: z.number(),
});

/** Pull `max_chars` / `actual_chars` out of a parsed error body shaped like
 * `{ detail: { code: "message_too_long", max_chars, actual_chars, ... } }`.
 * Returns `null` for any other shape so callers fall back to their generic
 * handling — mirrors `parseLlmCostLimitScope` in `llm-cost-limits.ts`. */
export function parseMessageTooLongDetail(
  payload: unknown,
): { maxChars: number; actualChars: number } | null {
  if (!payload || typeof payload !== "object" || !("detail" in payload)) return null;
  const parsed = messageTooLongDetailSchema.safeParse((payload as { detail: unknown }).detail);
  if (!parsed.success) return null;
  return { maxChars: parsed.data.max_chars, actualChars: parsed.data.actual_chars };
}

/** User-facing notice for a `message_too_long` rejection: names the limit
 * and the message's actual length, and suggests the existing attachment
 * upload as an alternative to pasting the text directly. */
export function messageTooLongMessage(maxChars: number, actualChars: number): string {
  return `El mensaje supera el límite de ${maxChars.toLocaleString("es")} caracteres (tiene ${actualChars.toLocaleString("es")}). Recórtalo o súbelo como archivo adjunto en lugar de pegarlo en el mensaje.`;
}

/** Thrown by `chatErrorFor` (`lib/chat.ts`) when `POST /chat` rejects a
 * message for exceeding the effective length cap, so the route renders a
 * specific notice instead of the generic `Chat request failed: 422`
 * message. Its `message` is already the user-facing notice, the same
 * convention `LlmCostLimitError` uses. */
export class MessageTooLongError extends Error {
  readonly maxChars: number;
  readonly actualChars: number;

  constructor(maxChars: number, actualChars: number) {
    super(messageTooLongMessage(maxChars, actualChars));
    this.name = "MessageTooLongError";
    this.maxChars = maxChars;
    this.actualChars = actualChars;
  }
}
